import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchManifest, upsertPin, attachMemory, createNote, completeNote,
  deletePin, deleteMemory, addBucket, completeBucket, removeBucket,
  type UpsertPinInput, type AttachMemoryInput, type CreateNoteInput, type CompleteNoteInput,
  type AddBucketInput,
} from './api';
import { applyClientClock, nextUnlockMoment } from './unlock-engine';
import { supabase } from './supabase';
import { enqueue } from './offline-queue';
import type { Manifest, CachedManifest, Member } from '@/types';

const MANIFEST_KEY = ['manifest'] as const;

/**
 * The hook the whole app reads from. Returns:
 *   - data: the manifest with client-clock unlock states applied
 *   - isFresh: whether we've round-tripped to the server this session
 *   - lastSync: when we last got a 200 (not 304)
 *
 * Internally:
 *   - Returns the cached IndexedDB copy synchronously on first paint
 *   - Revalidates with If-None-Match
 *   - Subscribes to Supabase Realtime for `manifest:bump` to refetch on push
 *   - Schedules a single setTimeout to re-render at the next unlock moment
 */
export function useManifest() {
  const qc = useQueryClient();
  const query = useQuery<CachedManifest | null>({
    queryKey: MANIFEST_KEY,
    queryFn: async () => {
      const { cached, fresh } = await fetchManifest();
      // Hand the cached copy back synchronously by setting it as initial,
      // then await fresh. TanStack Query will rerender with both pulses.
      if (cached) qc.setQueryData(MANIFEST_KEY, cached);
      return await fresh;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // Realtime: server pushes `bump` on the `manifest` channel after writes.
  useEffect(() => {
    const channel = supabase.channel('manifest', { config: { broadcast: { self: false } } });
    channel.on('broadcast', { event: 'bump' }, () => {
      void qc.invalidateQueries({ queryKey: MANIFEST_KEY });
    }).subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [qc]);

  // Single-shot re-render at the next unlock moment, so a time-locked pin
  // visually flips the instant it should — without a setInterval.
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!query.data?.payload) return;
    if (timer.current) window.clearTimeout(timer.current);
    const at = nextUnlockMoment(query.data.payload);
    if (!at) return;
    const delay = Math.max(0, at - Date.now()) + 50;
    timer.current = window.setTimeout(() => {
      void qc.invalidateQueries({ queryKey: MANIFEST_KEY });
    }, delay);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [query.data?.payload, qc]);

  // Apply the client clock to derive `current_state` against now() — bridges
  // the gap between unlock_at and the next manifest revalidation.
  const data = useMemo<Manifest | undefined>(
    () => (query.data?.payload ? applyClientClock(query.data.payload) : undefined),
    [query.data?.payload],
  );

  return {
    data,
    isFresh: !query.isFetching && !!query.data,
    isLoading: query.isLoading && !query.data,
    // Background revalidation in flight while we already have cached data —
    // distinct from the cold-start `isLoading`. Drives the brand-bar sync dot.
    isSyncing: query.isFetching && !!query.data,
    error: query.error,
    version: query.data?.version,
    refetch: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  };
}

export function useUpsertPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPinInput) => {
      try {
        return await upsertPin(input);
      } catch (err) {
        // Offline or server unreachable → queue, treat success.
        await enqueue({
          idempotency_key: input.idempotency_key,
          endpoint: '/pins', method: 'POST', body: input,
        });
        return { id: input.id ?? input.idempotency_key, queued: true } as { id: string };
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

export function useAttachMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AttachMemoryInput) => {
      try {
        return await attachMemory(input);
      } catch (err) {
        await enqueue({
          idempotency_key: input.idempotency_key,
          endpoint: '/memories', method: 'POST', body: input,
        });
        return { id: input.idempotency_key, queued: true } as { id: string };
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      try {
        return await createNote(input);
      } catch (err) {
        await enqueue({
          idempotency_key: input.idempotency_key,
          endpoint: '/notes', method: 'POST', body: input,
        });
        return { id: input.idempotency_key, queued: true } as { id: string };
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

export function useCompleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompleteNoteInput) => {
      try {
        return await completeNote(input);
      } catch (err) {
        await enqueue({
          idempotency_key: input.idempotency_key,
          endpoint: `/notes/${input.note_id}/complete`, method: 'POST', body: input,
        });
        return { id: input.note_id, queued: true } as { id: string };
      }
    },
    // Optimistic remove: pull the note out of the manifest immediately, so
    // the strip animates the sticky away without waiting for the round-trip.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            notes: prev.payload.notes.filter((n) => n.id !== vars.note_id),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

/**
 * One-tap reply: posts the reply, completes the original, and short-circuits
 * the home strip optimistically so the original sticky animates away
 * before the round-trip lands. Both writes share an idempotency_key cluster
 * so a mid-flight retry never doubles up.
 */
export function useQuickReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      original_id: string;
      to_user: string;
      body: string;
    }) => {
      const replyKey = crypto.randomUUID();
      const completeKey = crypto.randomUUID();
      try {
        await createNote({
          idempotency_key: replyKey,
          to_user: args.to_user,
          body: args.body,
          reply_to: args.original_id,
        });
        await completeNote({
          idempotency_key: completeKey,
          note_id: args.original_id,
        });
      } catch {
        await enqueue({
          idempotency_key: replyKey,
          endpoint: '/notes', method: 'POST',
          body: { idempotency_key: replyKey, to_user: args.to_user, body: args.body, reply_to: args.original_id },
        });
        await enqueue({
          idempotency_key: completeKey,
          endpoint: `/notes/${args.original_id}/complete`, method: 'POST',
          body: { idempotency_key: completeKey },
        });
      }
      return { id: args.original_id };
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            notes: prev.payload.notes.filter((n) => n.id !== vars.original_id),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

/**
 * Optimistic pin deletion. Pin disappears from the manifest cache instantly;
 * if the network call fails, we restore from the rollback snapshot.
 */
export function useDeletePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePin(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            pins: prev.payload.pins.filter((p) => p.id !== id),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

/**
 * Optimistic memory deletion. Removes the memory from its parent pin in cache.
 */
export function useDeleteMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMemory(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            pins: prev.payload.pins.map((p) => ({
              ...p,
              memories: p.memories.filter((m) => m.id !== id),
            })),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

// ── bucket list ───────────────────────────────────────────────────────────
export function useAddToBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddBucketInput) => addBucket(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

export function useCompleteBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { bucket_id: string; pin_id: string }) =>
      completeBucket({ ...args, idempotency_key: crypto.randomUUID() }),
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            bucket_list: prev.payload.bucket_list.map((b) =>
              b.id === args.bucket_id ? { ...b, completed: true } : b),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

export function useRemoveFromBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeBucket(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: MANIFEST_KEY });
      const prev = qc.getQueryData<CachedManifest>(MANIFEST_KEY);
      if (prev) {
        qc.setQueryData<CachedManifest>(MANIFEST_KEY, {
          ...prev,
          payload: {
            ...prev.payload,
            bucket_list: prev.payload.bucket_list.filter((b) => b.id !== id),
          },
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(MANIFEST_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: MANIFEST_KEY }),
  });
}

/**
 * Returns the current Supabase user's id and the resolved Member record,
 * so components can render "from her" / "from you" without comparing UUIDs
 * inline. Lightweight — `getUser` reads from local session.
 */
export function useCurrentMember(members: Member[] | undefined) {
  const [uid, setUid] = useState<string | null>(null);
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUid(data.user?.id ?? null));
  }, []);
  return useMemo(() => {
    if (!uid || !members) return { uid: null, me: null, other: null };
    const me    = members.find((m) => m.id === uid) ?? null;
    const other = members.find((m) => m.id !== uid) ?? null;
    return { uid, me, other };
  }, [uid, members]);
}
